import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useProject } from '@/features/projects/overview/use-project.query';
import { projectIdState } from '@/features/projects/project.state';
import { useUserAccount } from '@/user/use-user-account';
import { useAtomValue } from 'jotai';

import {
  mapNewUsersToPayload,
  mapTeamIdsToPayload,
  mapUserIdsToPayload,
} from '../helpers/map-share-payload';
import { ShareModal } from '../share-modal';
import type { ShareWithData } from '../share-with-select/share-with';

import { useShareProject } from './use-share-project';
import { useUsersAndTeamsWithAccessToProject } from './use-users-and-teams-with-access-to-project';

export const ShareProjectModal = () => {
  const { t } = useTranslation('default');
  const projectId = useAtomValue(projectIdState);
  const { data: project } = useProject(projectId);
  const userAccount = useUserAccount();

  const { shareProject } = useShareProject();
  const { isLoading, usersAndTeamsWithAccessToProject } =
    useUsersAndTeamsWithAccessToProject();

  const [shareWithData, setShareWithData] = useState<ShareWithData>({
    draftOptions: [],
    removedOptions: [],
    isPublic: project ? project.public : null,
    ownerId: project?.creator?.id,
  });

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setShareWithData(prevState => ({
      ...prevState,
      draftOptions: usersAndTeamsWithAccessToProject,
      isPublic: project ? project.public : null,
    }));
  }, [isLoading]);

  const handleSubmit = async () => {
    if (!project) return;
    const { draftOptions, isPublic } = shareWithData;

    await shareProject({
      public: Boolean(isPublic),
      newUsers: mapNewUsersToPayload(draftOptions),
      teamIds: mapTeamIdsToPayload(draftOptions),
      userIds: mapUserIdsToPayload(draftOptions),
      projectId: project.id,
    });
  };

  return (
    <ShareModal
      entitiesWithAccess={usersAndTeamsWithAccessToProject}
      isLoading={isLoading}
      title={t`share.shareProject`}
      privacyDefaultValue={Boolean(project?.public)}
      privacyPublicLabel={
        <Trans
          ns="default"
          i18nKey="share.projectPublicLabel"
          values={{ accountName: userAccount?.name }}
          components={{ u: <u /> }}
        />
      }
      privacyPrivateLabel={t`share.projectPrivateLabel`}
      onChange={setShareWithData}
      shareWithData={shareWithData}
      onSubmit={handleSubmit}
    />
  );
};
